import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Flex, Text } from '@radix-ui/themes';

// Loading component
const LoadingScreen: React.FC = () => (
  <Flex 
    direction="column"
    justify="center" 
    align="center" 
    className="min-h-screen"
    style={{
      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
    }}
  >
    <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mb-4"></div>
    <Text size="5" style={{ color: 'white' }}>
      Verifying access...
    </Text>
  </Flex>
);

/**
 * InternalOnlyRoute - Protects routes that should only be accessible to internal users
 * 
 * This component checks if the user is authenticated AND has internal access.
 * If the user is not authenticated, they will see the login screen (handled by parent ProtectedRoute).
 * If the user is authenticated but not internal, they will be silently redirected to home.
 * 
 * Usage:
 * <Route path="/admin" element={
 *   <ProtectedRoute>
 *     <InternalOnlyRoute>
 *       <AdminComponent />
 *     </InternalOnlyRoute>
 *   </ProtectedRoute>
 * } />
 */
export const InternalOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Show loading state while checking authentication
  if (loading) {
    return <LoadingScreen />;
  }
  
  // If user is not internal, silently redirect to home
  // (user should already be authenticated by parent ProtectedRoute)
  if (user && !user.is_internal) {
    console.log('⚠️ Non-internal user attempted to access internal-only route, redirecting to home');
    return <Navigate to="/" replace />;
  }
  
  // If user is internal, render the protected content
  return <>{children}</>;
};

export default InternalOnlyRoute;
